import React from "react";
import { Package, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

const CartItemsSection = ({ items = [], exchangeItems = [] }) => {
  return (
    <>
      {/* Regular Items */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" />
          Cart Items ({items.length})
        </h2>

        {items.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No items in cart</p>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item._id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex gap-4">
                  {/* Product Image */}
                  {item.product?.images?.[0] && (
                    <img
                      src={item.product.images[0].url}
                      alt={item.product.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}

                  {/* Product Details */}
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">
                      {item.product?.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      SKU: {item.product?.sku} | ID: {item.product?.productId}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm">
                      <span>
                        <span className="text-gray-600">Qty:</span>{" "}
                        <span className="font-medium">{item.quantity}</span>
                      </span>
                      <span>
                        <span className="text-gray-600">Weight:</span>{" "}
                        <span className="font-medium">
                          {item.product?.grossWeight}g
                        </span>
                      </span>
                      <span>
                        <span className="text-gray-600">Purity:</span>{" "}
                        <span className="font-medium">
                          {item.product?.metalDetails?.purity}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      ₹{item.pricing?.totalPrice?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      ₹{item.pricing?.unitPrice?.toLocaleString()} each
                    </p>

                    {/* Stock Status */}
                    {item.stock?.canFulfill ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-2">
                        <CheckCircle className="w-3 h-3" />
                        In Stock
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600 mt-2">
                        <XCircle className="w-3 h-3" />
                        Out of Stock
                      </span>
                    )}
                  </div>
                </div>

                {/* Applied Offers */}
                {item.appliedOffers?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-600">Applied Offers:</p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {item.appliedOffers.map((offer, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded"
                        >
                          {offer}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Exchange Items */}
      {exchangeItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Exchange Items ({exchangeItems.length})
          </h2>

          <div className="space-y-4">
            {exchangeItems.map((item) => (
              <div
                key={item._id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">
                      {item.description}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Type: {item.itemType} | Purity:{" "}
                      {item.metalDetails?.purity}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm">
                      <span>
                        <span className="text-gray-600">Gross:</span>{" "}
                        <span className="font-medium">
                          {item.weight?.gross}g
                        </span>
                      </span>
                      <span>
                        <span className="text-gray-600">Net:</span>{" "}
                        <span className="font-medium">{item.weight?.net}g</span>
                      </span>
                      {item.weight?.stone > 0 && (
                        <span>
                          <span className="text-gray-600">Stone:</span>{" "}
                          <span className="font-medium">
                            {item.weight.stone}g
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right ml-4">
                    {item.status === "VERIFIED" ? (
                      <>
                        <p className="text-lg font-semibold text-green-600">
                          ₹{item.valuation?.finalValue?.toLocaleString()}
                        </p>
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </span>
                      </>
                    ) : item.status === "REJECTED" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600">
                        <XCircle className="w-3 h-3" />
                        Rejected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-yellow-600">
                        <AlertTriangle className="w-3 h-3" />
                        Pending
                      </span>
                    )}
                  </div>
                </div>

                {/* Certification Info */}
                {(item.hallmark || item.certification) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-4 text-xs">
                    {item.hallmark && (
                      <span className="text-gray-600">
                        HUID: {item.hallmark.huid}
                      </span>
                    )}
                    {item.certification && (
                      <span className="text-gray-600">
                        Certificate: {item.certification.number}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default CartItemsSection;
